import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Boxes, ArrowLeft } from "lucide-react";
import { api, apiError, gbp, num, dateTime } from "@/lib/api";
import { AvailabilityBlock, availabilityMeta } from "@/components/Availability";
import { AppShell, PageHeader, Panel, SkeletonRows, EmptyState, Btn, Tag } from "@/components/Shell";
import { SwapPrompt } from "@/components/SwapPrompt";

const HEAD = ["on_hand", "committed", "available", "incoming"];

export function Products() {
  const [state, setState] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { page, page_size: pageSize, search } });
      setState(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const pages = Math.max(Math.ceil(state.total / pageSize), 1);

  return (
    <AppShell>
      <PageHeader
        title="Inventory"
        subtitle="On Hand, Committed, Available and Incoming per SKU — Available is always On Hand minus Committed."
        testId="products-header"
        actions={
          <form
            onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}
            className="flex items-center border border-slate-300 bg-white"
          >
            <Search className="ml-2 h-4 w-4 text-slate-400" />
            <input
              data-testid="product-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search SKU or name"
              className="w-56 px-2 py-2 text-sm outline-none"
            />
            <button data-testid="product-search-button" className="bg-slate-900 px-3 py-2 text-sm text-white">Search</button>
          </form>
        }
      />

      <Panel testId="products-panel" title={`${num(state.total)} products`}>
        {loading ? (
          <SkeletonRows rows={8} />
        ) : state.items.length === 0 ? (
          <EmptyState
            icon={Boxes}
            title="No products yet"
            body="Import items from Xero during onboarding, or add a product manually."
            testId="products-empty"
            action={<Link to="/onboarding" className="bg-slate-900 px-3 py-2 text-sm text-white" data-testid="products-empty-cta">Open setup</Link>}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="products-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">SKU</th>
                  <th className="px-4 py-2 text-left">Name</th>
                  {HEAD.map((k) => (
                    <th key={k} className="px-3 py-2 text-right">
                      <span className="inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: availabilityMeta[k].color }} />
                        {availabilityMeta[k].label}
                      </span>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right">Reorder</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {state.items.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50" data-testid={`product-row-${p.sku}`}>
                    <td className="px-4 py-2">
                      <Link to={`/app/products/${p.id}`} className="mono text-slate-900 underline-offset-2 hover:underline" data-testid={`product-link-${p.sku}`}>
                        {p.sku}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-700">
                      {p.name} {p.is_bundle && <Tag tone="violet">Bundle</Tag>}
                    </td>
                    {HEAD.map((k) => (
                      <td key={k} className="num px-3 py-2 text-right" data-testid={`product-${p.sku}-${k}`}
                          style={{ color: k === "available" && p[k] < 0 ? "#EF4444" : availabilityMeta[k].color }}>
                        {num(p[k])}
                      </td>
                    ))}
                    <td className="num px-3 py-2 text-right text-slate-500">{num(p.reorder_point)}</td>
                    <td className="num px-3 py-2 text-right text-slate-700">{gbp(p.unit_cost_pence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2.5 text-xs text-slate-500">
          <span>Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Btn variant="outline" data-testid="products-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Btn>
            <Btn variant="outline" data-testid="products-next-page" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Btn>
          </div>
        </div>
      </Panel>
    </AppShell>
  );
}

export function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [adj, setAdj] = useState({ quantity: "", reason: "stocktake", note: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/products/${id}`);
      setP(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitAdjustment = async () => {
    try {
      await api.post("/adjustments", {
        product_id: id,
        quantity: parseInt(adj.quantity, 10),
        reason: adj.reason,
        note: adj.note,
      });
      toast.success("Adjustment applied and journalled to Xero");
      setAdj({ quantity: "", reason: "stocktake", note: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!p) {
    return (
      <AppShell>
        <div className="border border-slate-200 bg-white"><SkeletonRows rows={6} /></div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <button onClick={() => navigate("/app/products")} className="mb-3 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="back-to-products">
        <ArrowLeft className="h-3.5 w-3.5" /> Inventory
      </button>
      <PageHeader
        title={p.name}
        subtitle={<span className="mono">{p.sku}</span>}
        testId="product-detail-header"
        actions={
          <>
            <Tag tone="slate">Cost {gbp(p.unit_cost_pence)}</Tag>
            <Tag tone="slate">Price {gbp(p.sale_price_pence)}</Tag>
            {p.is_bundle && <Tag tone="violet">Bundle</Tag>}
            {p.xero_tracked_on_import && <Tag tone="amber">Was tracked in Xero</Tag>}
          </>
        }
      />

      <div className="space-y-4">
        {(p.shortfall > 0 || p.available < 0) && !dismissed && (
          <SwapPrompt
            productId={id}
            sku={p.sku}
            shortfall={p.shortfall}
            dismissible
            onDismiss={async () => {
              setDismissed(true);
              try {
                await api.post("/substitutions/dismiss", { product_id: id });
              } catch {
                /* dismissal is best-effort */
              }
            }}
            onLearned={load}
          />
        )}

        <Panel title="Availability" testId="product-availability">
          <AvailabilityBlock data={p} testIdPrefix="product-avail" />
          {p.shortfall > 0 && (
            <p className="border-t border-slate-200 bg-red-50 px-4 py-2 text-xs text-red-700" data-testid="product-shortfall">
              Shortfall of {num(p.shortfall)} units against a reorder point of {num(p.reorder_point)}.
            </p>
          )}
        </Panel>

        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="By location" testId="product-locations">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Location</th>
                  <th className="px-3 py-2 text-right">On hand</th>
                  <th className="px-3 py-2 text-right">Committed</th>
                  <th className="px-3 py-2 text-right">Available</th>
                  <th className="px-3 py-2 text-right">Incoming</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {p.by_location.map((l) => (
                  <tr key={l.location_id}>
                    <td className="px-4 py-2">{l.location_name}</td>
                    <td className="num px-3 py-2 text-right">{num(l.on_hand)}</td>
                    <td className="num px-3 py-2 text-right">{num(l.committed)}</td>
                    <td className="num px-3 py-2 text-right">{num(l.available)}</td>
                    <td className="num px-3 py-2 text-right">{num(l.incoming)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel title="Substitutes" testId="product-alternatives">
            {p.alternatives.length === 0 ? (
              <EmptyState title="No substitutes configured" body="Add an alternative so the gap engine can suggest cover." testId="alternatives-empty" />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">SKU</th>
                    <th className="px-3 py-2 text-right">Ratio</th>
                    <th className="px-3 py-2 text-left">Direction</th>
                    <th className="px-3 py-2 text-right">Priority</th>
                    <th className="px-3 py-2 text-right">Available</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {p.alternatives.map((a) => (
                    <tr key={a.product_id} data-testid={`alternative-row-${a.sku}`}>
                      <td className="px-4 py-2">
                        <Link to={`/app/products/${a.product_id}`} className="mono hover:underline">{a.sku}</Link>
                      </td>
                      <td className="num px-3 py-2 text-right">{a.ratio}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{a.direction?.replace("_", " ")}</td>
                      <td className="num px-3 py-2 text-right">{a.priority}</td>
                      <td className="num px-3 py-2 text-right">{num(a.available)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>

        <Panel title="Adjust stock" testId="product-adjust">
          <div className="flex flex-wrap items-end gap-3 p-4">
            <label className="text-sm">
              Quantity (+/−)
              <input
                data-testid="adjust-quantity-input"
                value={adj.quantity}
                onChange={(e) => setAdj((a) => ({ ...a, quantity: e.target.value }))}
                className="mono mt-1 block w-28 border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                placeholder="-5"
              />
            </label>
            <label className="text-sm">
              Reason code
              <select
                data-testid="adjust-reason-select"
                value={adj.reason}
                onChange={(e) => setAdj((a) => ({ ...a, reason: e.target.value }))}
                className="mt-1 block border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              >
                {["stocktake", "damaged", "lost", "found", "return_to_supplier", "sample", "correction"].map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
                ))}
              </select>
            </label>
            <label className="flex-1 text-sm">
              Note
              <input
                data-testid="adjust-note-input"
                value={adj.note}
                onChange={(e) => setAdj((a) => ({ ...a, note: e.target.value }))}
                className="mt-1 block w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              />
            </label>
            <Btn data-testid="adjust-submit-button" disabled={!adj.quantity} onClick={submitAdjustment}>
              Apply & journal
            </Btn>
          </div>
        </Panel>

        <Panel title="Movement ledger" testId="product-movements">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Event</th>
                  <th className="px-3 py-2 text-right">On hand Δ</th>
                  <th className="px-3 py-2 text-right">Committed Δ</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Note</th>
                  <th className="px-3 py-2 text-left">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {p.movements.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2"><Tag>{m.type.replace(/[:_]/g, " ")}</Tag></td>
                    <td className="num px-3 py-2 text-right">{m.qty_on_hand || "—"}</td>
                    <td className="num px-3 py-2 text-right">{m.qty_committed || "—"}</td>
                    <td className="num px-3 py-2 text-right">{num(m.balance_on_hand)}</td>
                    <td className="mono px-3 py-2 text-xs text-slate-500">{m.reference || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{m.note}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{dateTime(m.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </AppShell>
  );
}
